import { DURATION_UNIT_SECONDS, type DurationUnit } from '@aysar/contracts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { formatNumber } from '@/shared/utils/format-number/format-number.util';

import {
  bestUnitFor,
  offeredUnits,
  toBase,
  toDisplay,
} from '../duration-scale/duration-scale.util';
import { useScalarAnnouncer } from '../useScalarAnnouncer/useScalarAnnouncer';
import { useScalarNumber, type UseScalarNumber } from '../useScalarNumber/useScalarNumber';

import { type DurationInputProps } from './DurationInput.types';

export type UseDurationInputLogicInput = Omit<
  DurationInputProps,
  'id' | 'className' | 'size' | 'aria-describedby'
>;

export interface UseDurationInputLogic {
  number: UseScalarNumber;
  /** The unit currently on screen. Never what the value is stored in, necessarily. */
  unit: DurationUnit;
  units: readonly DurationUnit[];
  onUnitChange: (unit: DurationUnit) => void;
  unitName: (unit: DurationUnit) => string;
  isForever: boolean;
  canBeForever: boolean;
  onForeverChange: (next: boolean) => void;
  /** What the platform will actually store — always in the base unit. */
  storedHint: string;
  rangeHint: string | undefined;
  /** Why the amount on screen cannot be stored, when it cannot. */
  warning: string | undefined;
  /** The largest unit that WOULD hold the refused amount, if there is one. */
  adoptUnit: DurationUnit | undefined;
  adoptLabel: string | undefined;
  onAdopt: () => void;
  announcement: string;
  amountLabel: string;
  unitLabel: string;
  foreverLabel: string;
}

/** How many `to` units one `from` unit is. */
function ratio(from: DurationUnit, to: DurationUnit): number {
  return DURATION_UNIT_SECONDS[from] / DURATION_UNIT_SECONDS[to];
}

/**
 * An integer duration, edited in whatever unit reads best.
 *
 * Eight settings were bare integers in three different base units, so `900` and
 * `900` meant fifteen minutes and two and a half years and looked identical.
 * Here the operator edits "15 minutes" and reads "stored as 900 seconds", and
 * the unit dropdown changes only what is on screen — **switching it emits
 * nothing**, which is the invariant that keeps every stored duration safe.
 *
 * The offered ladder guarantees the amount on screen is always a whole number
 * (see `offeredUnits`), so a fraction can only arrive by being typed. It is
 * **refused rather than rounded**: rounding would silently store 900 seconds for
 * a typed 15.5 minutes. Instead the control names the problem and offers the
 * unit that can hold it — a refusal with a way out of it.
 */
export function useDurationInputLogic({
  label,
  value,
  onValueChange,
  baseUnit,
  min,
  max,
  units,
  isNullable = false,
  isReadOnly = false,
  isDisabled = false,
}: UseDurationInputLogicInput): UseDurationInputLogic {
  const { t } = useTranslation('uiKit');
  const { locale } = useDirection();
  const announcer = useScalarAnnouncer();

  const ladder = useMemo(
    () => units ?? offeredUnits(baseUnit, { min, max }),
    [baseUnit, max, min, units],
  );

  const [preferred, setPreferred] = useState<DurationUnit>(() =>
    bestUnitFor(value, baseUnit, ladder),
  );

  // Derived rather than synchronised in an effect: a value arriving from outside
  // (a refetch, a form reset) that the chosen unit cannot express exactly falls
  // back to one that can, without a render-time side effect. It is a re-render
  // of someone else's change, not a refusal of the operator's, so it is silent —
  // the dropdown visibly moves, and the hint says what is stored either way.
  const unit =
    value !== null && value !== undefined && value % ratio(preferred, baseUnit) !== 0
      ? bestUnitFor(value, baseUnit, ladder)
      : preferred;

  const isForever = isNullable && value === null;

  const refuse = useCallback((): boolean => {
    if (isDisabled) return true;
    if (!isReadOnly) return false;
    announcer.announceReadOnlyRefusal();
    return true;
  }, [announcer, isDisabled, isReadOnly]);

  const commit = useCallback(
    (amount: number | undefined) => {
      onValueChange(amount === undefined ? undefined : toBase(amount, unit, baseUnit));
    },
    [baseUnit, onValueChange, unit],
  );

  // `undefined` all the way through rather than a converted `NaN`: an empty box
  // and a "forever" box both have NO amount, and `String(NaN)` would print the
  // word into the field.
  const displayValue =
    isForever || value === null || value === undefined
      ? undefined
      : toDisplay(value, baseUnit, unit);

  const number = useScalarNumber({
    displayValue,
    step: 1,
    isIntegerOnly: true,
    min: min === undefined ? undefined : toDisplay(min, baseUnit, unit),
    max: max === undefined ? undefined : toDisplay(max, baseUnit, unit),
    isReadOnly,
    isDisabled,
    onCommit: commit,
    onRefuse: announcer.announceReadOnlyRefusal,
  });

  const onUnitChange = useCallback(
    (next: DurationUnit) => {
      // Read-only refuses this too: it changes what is on screen, and a control
      // that says "read-only" while one of its three parts still moves is worse
      // than one that says nothing.
      if (refuse()) return;
      number.reset();
      setPreferred(next);
    },
    [number, refuse],
  );

  const onForeverChange = useCallback(
    (next: boolean) => {
      if (refuse()) return;
      number.reset();
      // `undefined`, not `0`: unchecking means "there is no value here yet",
      // and `0` is a legitimate, very different answer.
      onValueChange(next ? null : undefined);
    },
    [number, onValueChange, refuse],
  );

  /** The largest offered unit below the current one that holds the refused draft. */
  const adoptUnit = useMemo(() => {
    const draft = number.draftValue;
    if (!number.isDraftInvalid || draft === undefined || !Number.isFinite(draft)) return undefined;

    return [...ladder]
      .filter((candidate) => DURATION_UNIT_SECONDS[candidate] < DURATION_UNIT_SECONDS[unit])
      .sort((a, b) => DURATION_UNIT_SECONDS[b] - DURATION_UNIT_SECONDS[a])
      .find((candidate) => Number.isInteger(draft * ratio(unit, candidate)));
  }, [ladder, number.draftValue, number.isDraftInvalid, unit]);

  const counted = useCallback(
    (amount: number, forUnit: DurationUnit): string =>
      t(`duration.unitCount.${forUnit}`, {
        count: amount,
        // i18next selects the plural from `count` but prints it verbatim. The
        // formatted copy is what reaches the sentence, so Arabic gets correct
        // grammar AND the portal's canonical Latin digits.
        value: formatNumber(amount, locale),
      }),
    [locale, t],
  );

  const onAdopt = useCallback(() => {
    const draft = number.draftValue;
    if (refuse() || adoptUnit === undefined || draft === undefined) return;

    const stored = toBase(draft, unit, baseUnit);
    number.reset();
    setPreferred(adoptUnit);
    onValueChange(stored);
    announcer.say(t('duration.adopted', { value: counted(stored, baseUnit) }));
  }, [adoptUnit, announcer, baseUnit, counted, number, onValueChange, refuse, t, unit]);

  const unitName = useCallback(
    (forUnit: DurationUnit): string => t(`duration.unitName.${forUnit}`),
    [t],
  );

  const storedHint = useMemo(() => {
    if (isForever) return t('duration.foreverOn');
    if (value === null || value === undefined) return t('scalar.empty');
    if (value === 0) return t('duration.zeroMeaning');
    return t('duration.stored', { value: counted(value, baseUnit) });
  }, [baseUnit, counted, isForever, t, value]);

  const rangeHint = useMemo(() => {
    if (min === undefined && max === undefined) return undefined;

    const low = min === undefined ? undefined : counted(toDisplay(min, baseUnit, unit), unit);
    const high = max === undefined ? undefined : counted(toDisplay(max, baseUnit, unit), unit);

    if (low !== undefined && high !== undefined)
      return t('duration.range', { min: low, max: high });
    if (low !== undefined) return t('duration.rangeMin', { min: low });
    return t('duration.rangeMax', { max: high });
  }, [baseUnit, counted, max, min, t, unit]);

  return useMemo(
    () => ({
      number,
      unit,
      units: ladder,
      onUnitChange,
      unitName,
      isForever,
      canBeForever: isNullable,
      onForeverChange,
      storedHint,
      rangeHint,
      warning: number.isDraftInvalid ? t('duration.wholeOnly') : undefined,
      adoptUnit,
      adoptLabel:
        adoptUnit === undefined ? undefined : t('duration.useUnit', { unit: unitName(adoptUnit) }),
      onAdopt,
      announcement: announcer.announcement,
      amountLabel: t('duration.amountFor', { label }),
      unitLabel: t('duration.unitFor', { label }),
      foreverLabel: t('duration.foreverFor', { label }),
    }),
    [
      adoptUnit,
      announcer.announcement,
      isForever,
      isNullable,
      label,
      ladder,
      number,
      onAdopt,
      onForeverChange,
      onUnitChange,
      rangeHint,
      storedHint,
      t,
      unit,
      unitName,
    ],
  );
}
